import type { DeliveryCity, DeliveryKey, PaymentMethodKey } from '@c214/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DeliveryStep } from '../components/checkout/DeliveryStep';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { PaymentStep } from '../components/checkout/PaymentStep';
import { TelegramConnect } from '../components/notifications/TelegramConnect';
import { Button } from '../components/ui/button';
import { useAuth } from '../hooks/useAuth';
import { useCreateOrder } from '../hooks/useOrders';
import { useProfile } from '../hooks/useProfile';
import { useAuthModal, useCart } from '../providers';

export function CheckoutPage() {
  const { data: user, isLoading: authLoading } = useAuth();
  const { data: profile } = useProfile();
  const authModal = useAuthModal();
  const cart = useCart();
  const createOrder = useCreateOrder();

  const [step, setStep] = useState<1 | 2>(1);
  const [isSelf, setIsSelf] = useState(true);
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [patronymic, setPatronymic] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryKey, setDeliveryKey] = useState<DeliveryKey>('np_branch');
  const [city, setCity] = useState<DeliveryCity | null>(null);
  const [warehouseRef, setWarehouseRef] = useState<string | null>(null);
  const [warehouseAddress, setWarehouseAddress] = useState<string | null>(null);
  const [street, setStreet] = useState('');
  const [building, setBuilding] = useState('');
  const [apartment, setApartment] = useState('');
  const [floor, setFloor] = useState('');
  const [intercom, setIntercom] = useState('');
  const [paymentKey, setPaymentKey] = useState<PaymentMethodKey>('card');
  const [comment, setComment] = useState('');
  const [placedNumber, setPlacedNumber] = useState<number | null>(null);

  // Автозаповнення при "Я": підставляємо збережений профіль щойно він
  // прийде з сервера — але лише поки перемикач лишається на "Я", інакше
  // затерли б дані, які людина вже почала вводити для когось іншого.
  // Toggling isSelf itself is handled explicitly in the Chip's onClick
  // below, not by re-running this effect — reacts only to profile arriving.
  // Якщо збереженого профілю ще нема (наприклад, перше замовлення), але є
  // ім'я з Google-акаунту — підставляємо орієнтовний розбір цього імені;
  // це лише підказка в полях, профіль зберігається насправді тільки після
  // підтвердження замовлення.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (!isSelf) return;
    if (profile) {
      setLastName(profile.lastName);
      setFirstName(profile.firstName);
      setPatronymic(profile.patronymic ?? '');
      setPhone(profile.phone);
      return;
    }
    if (profile === null && user?.name) {
      const [guessedFirstName, ...rest] = user.name.trim().split(/\s+/);
      setFirstName(guessedFirstName ?? '');
      setLastName(rest.join(' '));
    }
  }, [profile, user]);

  if (authLoading) return <p className="section-pad mono-label">ЗАВАНТАЖЕННЯ…</p>;

  if (!user) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Увійдіть, щоб оформити замовлення.</p>
        <Button variant="accent" size="lg" onClick={() => authModal.open('login')}>
          УВІЙТИ
        </Button>
      </div>
    );
  }

  if (placedNumber !== null) {
    return (
      <div className="section-pad">
        <div className="mx-auto max-w-[480px] border border-accent p-8 text-center">
          <p className="text-[20px] font-bold text-ink">Замовлення №{placedNumber} прийнято</p>
          <p className="mt-3 text-[14px] text-muted">
            Обсмажимо у найближчу партію. Статус і ТТН надішлемо на пошту.
          </p>
          <div className="mt-6">
            <TelegramConnect />
          </div>
          <Link
            to="/catalog"
            className="mt-6 inline-block text-[14px] font-semibold text-accent no-underline"
          >
            ДО КАТАЛОГУ →
          </Link>
        </div>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="section-pad text-center">
        <p className="mb-4 text-[15px] text-muted">Кошик порожній.</p>
        <Link to="/catalog" className="text-[14px] font-semibold text-accent no-underline">
          ДО КАТАЛОГУ →
        </Link>
      </div>
    );
  }

  const handleSubmit = () => {
    createOrder.mutate(
      {
        items: cart.items.map((item) => ({
          productId: item.productId,
          weight: item.weight,
          grind: item.grind,
          qty: item.qty,
        })),
        deliveryKey,
        deliveryCity: city?.name,
        deliveryWarehouse: warehouseAddress ?? undefined,
        deliveryStreet: street.trim() || undefined,
        deliveryBuilding: building.trim() || undefined,
        deliveryApartment: apartment.trim() || undefined,
        deliveryFloor: floor.trim() || undefined,
        deliveryIntercom: intercom.trim() || undefined,
        recipientLastName: lastName,
        recipientFirstName: firstName,
        recipientPatronymic: patronymic.trim() || null,
        recipientPhone: phone,
        recipientIsSelf: isSelf,
        paymentKey,
        comment: comment.trim() || undefined,
      },
      {
        onSuccess: (order) => {
          if (order.checkoutUrl) {
            // Full-page navigation to Monobank's hosted checkout is about to
            // happen — don't clear the cart or show the "placed" confirmation
            // yet, the payment isn't confirmed. If the customer abandons
            // payment and comes back, their cart should still be here.
            // Stash the order number so OrdersPage can clear the cart once
            // this specific order's payment actually confirms (see there).
            try {
              localStorage.setItem('c214_pending_order', String(order.number));
            } catch {
              // приватний режим або заблоковане сховище — просто не чистимо кошик пізніше
            }
            window.location.href = order.checkoutUrl;
            return;
          }
          cart.clear();
          setPlacedNumber(order.number);
        },
      },
    );
  };

  return (
    <div className="section-pad grid gap-10 [grid-template-columns:repeat(auto-fit,minmax(320px,1fr))]">
      <div>
        <div className="mb-8 flex gap-6">
          <span className={step === 1 ? 'mono-label text-accent' : 'mono-label'}>01 ДОСТАВКА</span>
          <span className={step === 2 ? 'mono-label text-accent' : 'mono-label'}>02 ОПЛАТА</span>
        </div>

        {step === 1 ? (
          <DeliveryStep
            isSelf={isSelf}
            onIsSelfChange={(next) => {
              setIsSelf(next);
              if (!next) {
                setLastName('');
                setFirstName('');
                setPatronymic('');
                setPhone('');
                return;
              }
              if (profile) {
                setLastName(profile.lastName);
                setFirstName(profile.firstName);
                setPatronymic(profile.patronymic ?? '');
                setPhone(profile.phone);
              } else if (user?.name) {
                const [guessedFirstName, ...rest] = user.name.trim().split(/\s+/);
                setFirstName(guessedFirstName ?? '');
                setLastName(rest.join(' '));
              }
            }}
            lastName={lastName}
            onLastNameChange={setLastName}
            firstName={firstName}
            onFirstNameChange={setFirstName}
            patronymic={patronymic}
            onPatronymicChange={setPatronymic}
            phone={phone}
            onPhoneChange={setPhone}
            deliveryKey={deliveryKey}
            onDeliveryKeyChange={(key) => {
              setDeliveryKey(key);
              setWarehouseRef(null);
              setWarehouseAddress(null);
              setStreet('');
              setBuilding('');
              setApartment('');
              setFloor('');
              setIntercom('');
            }}
            city={city}
            onCityChange={(next) => {
              setCity(next);
              setWarehouseRef(null);
              setWarehouseAddress(null);
            }}
            warehouseRef={warehouseRef}
            onWarehouseChange={(ref, address) => {
              setWarehouseRef(ref);
              setWarehouseAddress(address);
            }}
            street={street}
            onStreetChange={setStreet}
            building={building}
            onBuildingChange={setBuilding}
            apartment={apartment}
            onApartmentChange={setApartment}
            floor={floor}
            onFloorChange={setFloor}
            intercom={intercom}
            onIntercomChange={setIntercom}
            onNext={() => setStep(2)}
          />
        ) : (
          <PaymentStep
            paymentKey={paymentKey}
            onPaymentKeyChange={setPaymentKey}
            comment={comment}
            onCommentChange={setComment}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
            isSubmitting={createOrder.isPending}
            submitError={createOrder.isError ? createOrder.error.message : null}
          />
        )}
      </div>

      <OrderSummary items={cart.items} deliveryKey={deliveryKey} />
    </div>
  );
}
